import { ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { api } from '@/api/client';
import { useIssuesStore } from '@/stores/issues';
/**
 * Composable that wraps GET /v1/issues with cursor pagination.
 * Re-fetches when filtersRef changes (watches deeply).
 *
 * Used by IssuesListView for the main list, and by RecentIssuesCard (dashboard D.2).
 */
export function useIssues(filtersRef) {
    const store = useIssuesStore();
    const { listIds, nextCursor, totalCount, openCount, criticalCount } = storeToRefs(store);
    const isLoading = ref(false);
    const isLoadingMore = ref(false);
    const error = ref(null);
    function buildQueryFromFilters(filters, cursor) {
        // Map tab to status/severity filters
        let statusParam;
        let severityParam;
        if (filters.tab === 'critical') {
            severityParam = '3'; // Critical
        }
        else if (filters.tab === 'resolved') {
            statusParam = '3'; // Resolved
        }
        else if (filters.tab === 'awaiting') {
            statusParam = '1'; // Acknowledged
        }
        // Explicit filters override tab-derived ones
        if (filters.status && filters.status.length > 0) {
            statusParam = filters.status.join(',');
        }
        if (filters.severity && filters.severity.length > 0) {
            severityParam = filters.severity.join(',');
        }
        return {
            status: statusParam,
            severity: severityParam,
            category: filters.category?.join(','),
            source: filters.source?.join(','),
            assignedToId: filters.assignedToId,
            aiConfidenceMin: filters.aiConfidenceMin,
            search: filters.search || undefined,
            cursor,
            limit: filters.limit ?? 25,
        };
    }
    async function fetch(isLoadMore = false) {
        if (isLoadMore) {
            isLoadingMore.value = true;
        }
        else {
            isLoading.value = true;
            error.value = null;
        }
        try {
            const cursor = isLoadMore ? (nextCursor.value ?? undefined) : undefined;
            const query = buildQueryFromFilters(filtersRef.value, cursor);
            const { data, error: apiError } = await api.GET('/v1/issues', {
                params: { query },
            });
            if (apiError || !data) {
                error.value = 'Failed to load issues.';
                return;
            }
            if (isLoadMore) {
                store.appendPage(data.items, data.nextCursor, data.totalCount, data.openCount, data.criticalCount);
            }
            else {
                store.setPage(data.items, data.nextCursor, data.totalCount, data.openCount, data.criticalCount);
            }
        }
        catch (e) {
            error.value = e instanceof Error ? e.message : 'Failed to load issues.';
        }
        finally {
            isLoading.value = false;
            isLoadingMore.value = false;
        }
    }
    function loadMore() {
        if (!nextCursor.value || isLoadingMore.value)
            return;
        void fetch(true);
    }
    function refresh() {
        void fetch(false);
    }
    async function patch(id, req) {
        // Optimistic update
        const existing = store.entitiesById.get(id);
        if (!existing)
            return;
        const optimistic = { ...existing, ...req };
        store.upsert(optimistic);
        try {
            const { data, error: apiError } = await api.PATCH('/v1/issues/{id}', {
                params: { path: { id } },
                body: req,
            });
            if (apiError || !data) {
                // Revert on error
                store.upsert(existing);
                throw new Error('Failed to update issue.');
            }
            store.upsert(data);
        }
        catch {
            store.upsert(existing);
            throw new Error('Failed to update issue.');
        }
    }
    // Watch filters deeply and re-fetch when they change.
    watch(filtersRef, () => { void fetch(false); }, { deep: true, immediate: true });
    return {
        listIds,
        isLoading,
        isLoadingMore,
        nextCursor,
        totalCount,
        openCount,
        criticalCount,
        error,
        loadMore,
        refresh,
        patch,
    };
}
